import { formatInTimeZone } from "date-fns-tz";
import { getAllCountries, getCountry } from "countries-and-timezones";

/**
 * IANA ids (`Europe/Dublin`, `America/Chicago`, …) are what `Intl` uses for correct local time + DST.
 *
 * - **Single-zone countries**: taken from `countries-and-timezones` using `countryName` → ISO → sole zone.
 * - **Multi-zone countries**: need **`cityName`** plus a row in `CITY_TO_IANA_BY_COUNTRY_AND_CITY`
 *   (`iso|city` → zone). You can’t derive that from strings alone without this table or a geocoding API.
 */

/** Multi-zone countries: `lowercaseIso2|normalizedCity` → IANA. See `normalizeCityName`. */
const CITY_TO_IANA_BY_COUNTRY_AND_CITY: Record<string, string> = {
  "us|new york": "America/New_York",
  "us|new york city": "America/New_York",
  "us|manhattan": "America/New_York",
  "us|boston": "America/New_York",
  "us|philadelphia": "America/New_York",
  "us|washington": "America/New_York",
  "us|washington dc": "America/New_York",
  "us|miami": "America/New_York",
  "us|atlanta": "America/New_York",
  "us|chicago": "America/Chicago",
  "us|houston": "America/Chicago",
  "us|dallas": "America/Chicago",
  "us|minneapolis": "America/Chicago",
  "us|austin": "America/Chicago",
  "us|st louis": "America/Chicago",
  "us|denver": "America/Denver",
  "us|phoenix": "America/Phoenix",
  "us|los angeles": "America/Los_Angeles",
  "us|san francisco": "America/Los_Angeles",
  "us|san diego": "America/Los_Angeles",
  "us|seattle": "America/Los_Angeles",
  "us|portland": "America/Los_Angeles",
  "us|las vegas": "America/Los_Angeles",
  "us|honolulu": "Pacific/Honolulu",
  "us|anchorage": "America/Anchorage",
  "ca|toronto": "America/Toronto",
  "ca|montreal": "America/Toronto",
  "ca|ottawa": "America/Toronto",
  "ca|vancouver": "America/Vancouver",
  "ca|calgary": "America/Edmonton",
  "ca|edmonton": "America/Edmonton",
  "ca|winnipeg": "America/Winnipeg",
  "ca|halifax": "America/Halifax",
  "au|sydney": "Australia/Sydney",
  "au|melbourne": "Australia/Melbourne",
  "au|brisbane": "Australia/Brisbane",
  "au|perth": "Australia/Perth",
  "au|adelaide": "Australia/Adelaide",
  "au|darwin": "Australia/Darwin",
  "br|são paulo": "America/Sao_Paulo",
  "br|sao paulo": "America/Sao_Paulo",
  "br|rio de janeiro": "America/Sao_Paulo",
  "br|brasília": "America/Sao_Paulo",
  "br|brasilia": "America/Sao_Paulo",
  "br|manaus": "America/Manaus",
  "mx|mexico city": "America/Mexico_City",
  "mx|ciudad de méxico": "America/Mexico_City",
  "mx|cancún": "America/Cancun",
  "mx|cancun": "America/Cancun",
  "mx|tijuana": "America/Tijuana",
  "id|jakarta": "Asia/Jakarta",
  "id|bali": "Asia/Makassar",
  "id|denpasar": "Asia/Makassar",
  "ru|moscow": "Europe/Moscow",
  "ru|saint petersburg": "Europe/Moscow",
  "ru|vladivostok": "Asia/Vladivostok",
  "es|las palmas": "Atlantic/Canary",
  "es|santa cruz de tenerife": "Atlantic/Canary",
};

/** Common English / admin aliases → ISO 3166-1 alpha-2 */
const COUNTRY_NAME_ALIASES_TO_ISO2: Record<string, string> = {
  usa: "US",
  "u.s.a.": "US",
  "u.s.": "US",
  "united states": "US",
  "united states of america": "US",
  uk: "GB",
  "united kingdom": "GB",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  uae: "AE",
  emirates: "AE",
  "south korea": "KR",
  "north korea": "KP",
  russia: "RU",
  "czech republic": "CZ",
  "czechia": "CZ",
  "ivory coast": "CI",
  "côte d'ivoire": "CI",
  "viet nam": "VN",
  gana: "GH",
  "republic of ghana": "GH",
  "the republic of ghana": "GH",
  "ghana republic": "GH",
};

/** Canonical IANA for single-zone countries when the dataset uses a linked zone (e.g. GH → Abidjan). */
const ISO2_PREFERRED_IANA_FOR_SINGLE_ZONE: Partial<Record<string, string>> = {
  GH: "Africa/Accra",
};

let countryDisplayNameToIso2: Map<string, string> | null = null;

function getCountryIso2FromDisplayName(countryName: string): string | undefined {
  const trimmed = countryName.trim();
  if (!trimmed) {
    return undefined;
  }

  // ISO 3166-1 alpha-2 stored as the “name” (e.g. catalog / API) — not in getAllCountries() by default.
  if (/^[a-z]{2}$/i.test(trimmed)) {
    const code = trimmed.toUpperCase();
    if (getCountry(code)) {
      return code;
    }
  }

  if (!countryDisplayNameToIso2) {
    const m = new Map<string, string>();
    const all = getAllCountries();
    for (const c of Object.values(all)) {
      m.set(c.name.trim().toLowerCase(), c.id);
    }
    for (const [alias, iso] of Object.entries(COUNTRY_NAME_ALIASES_TO_ISO2)) {
      m.set(alias, iso);
    }
    countryDisplayNameToIso2 = m;
  }

  const key = trimmed.toLowerCase();
  return countryDisplayNameToIso2.get(key);
}

/** Normalize city for lookup keys (lowercase, collapse spaces, strip periods). */
export function normalizeCityName(city: string): string {
  return city
    .trim()
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ");
}

/**
 * Resolve IANA from firm `countryName` and optional `cityName` (both from Cosmos; no extra reads).
 */
export function resolveIanaFromFirmCountryCity(firm: {
  countryName?: string;
  cityName?: string;
} | null | undefined): string | undefined {
  const countryName = firm?.countryName?.trim();
  if (!countryName) {
    return undefined;
  }

  const iso2 = getCountryIso2FromDisplayName(countryName);
  if (!iso2) {
    return undefined;
  }

  const country = getCountry(iso2);
  const zones = country?.timezones;
  if (!zones?.length) {
    return undefined;
  }

  if (zones.length === 1) {
    const preferred = ISO2_PREFERRED_IANA_FOR_SINGLE_ZONE[iso2];
    const z = preferred && isValidIanaTimeZone(preferred) ? preferred : zones[0];
    return isValidIanaTimeZone(z) ? z : undefined;
  }

  const cityRaw = firm?.cityName?.trim();
  if (!cityRaw) {
    return undefined;
  }

  const cityKey = normalizeCityName(cityRaw);
  const composite = `${iso2.toLowerCase()}|${cityKey}`;
  const mapped = CITY_TO_IANA_BY_COUNTRY_AND_CITY[composite];
  if (mapped && isValidIanaTimeZone(mapped)) {
    return mapped;
  }

  return undefined;
}

export function isValidIanaTimeZone(zone: string): boolean {
  const z = zone?.trim();
  if (!z) {
    return false;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: z });
    return true;
  } catch {
    return false;
  }
}

/**
 * Local wall time in the given IANA zone (DST handled by the environment’s tz database).
 */
export function formatFirmLocalTimeLine(ianaTimeZone: string, nowMs: number): string {
  if (!isValidIanaTimeZone(ianaTimeZone)) {
    return "";
  }
  try {
    const now = new Date(nowMs);
    const day = formatInTimeZone(now, ianaTimeZone, "EEEE");
    const hm = formatInTimeZone(now, ianaTimeZone, "HH:mm");
    return `Local Time: ${day} ${hm}`;
  } catch {
    return "";
  }
}
